import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import OpenInNew from "@mui/icons-material/OpenInNew";
import PreviewIcon from "@mui/icons-material/Preview";
import {
  Alert,
  Box,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  GetSubmissionQuery,
  SignatureType,
  SubmissionDocumentType,
  SubmissionStatus,
} from "generated-graphql/graphql";
import { SubmissionReviewState } from "hooks/submission/reviewStates";
import { type SignatureConfirmationState } from "hooks/submission/useSignatureConfirmation";
import { useCurrentUser } from "hooks/useCurrentUser";

import { brandColors } from "../../util";
import { EmbeddedDocumentSigning } from "./EmbeddedSignature/EmbeddedDocumentSigning";
import { SignatureConfirmationAlert } from "./SignatureConfirmationAlert";
import { SubmissionDocumentPreview } from "./SubmissionDocumentPreview";
import { SubmissionPaymentDisplay } from "./SubmissionPaymentDisplay";
import {
  SubmissionFacilityDisplayInfo,
  SubmissionReadOnlySummary,
} from "./SubmissionReadOnlySummary";

type Submission = NonNullable<GetSubmissionQuery["submission"]>;

interface DocumentDisplay {
  id: string;
  name: string;
  documentType: string;
  submissionDocumentType?: SubmissionDocumentType;
  url?: string;
}

interface SubmissionDisplayStaticProps {
  submission: Submission;
  reviewState: SubmissionReviewState;
  facilities: SubmissionFacilityDisplayInfo[];
  reviewers: Submission["reviewers"];
  signer: Submission["reviewers"][number] | undefined;
  submissionDocuments: DocumentDisplay[];
  attachmentDocuments: DocumentDisplay[];
  previewDocumentUrl: string | null;
  previewDocumentExtension?: string;
  signingCompleted: boolean;
  signingFlowConfirmed: boolean;
  signingConfirmationPending: boolean;
  isPreviewedDocumentPreparedForSigning: boolean;
  signatureConfirmationState: SignatureConfirmationState;
  isAwaitingPostSignatureTransition: boolean;
  signingDialogOpen: boolean;
  signingUrlData?: {
    getEmbeddedDocumentSigningUrl?: string | null | undefined;
  };
  tenantId: string;
  isStaff: boolean;
  onDocumentPreview: (document: { id: string; name?: string }) => void;
  onDocumentOpenInNewTab: (document: { id: string }) => void;
  onSigningDialogClose: () => void;
  onSigningDialogOpen: () => void;
  onSigningCompleted: () => void;
  onRetrySignatureConfirmation: () => void;
}

export function SubmissionDisplayStatic({
  submission,
  reviewState,
  facilities,
  reviewers,
  signer,
  submissionDocuments,
  attachmentDocuments,
  previewDocumentUrl,
  previewDocumentExtension,
  signingCompleted,
  signingFlowConfirmed,
  signingConfirmationPending,
  isPreviewedDocumentPreparedForSigning,
  signatureConfirmationState,
  isAwaitingPostSignatureTransition,
  signingDialogOpen,
  signingUrlData,
  tenantId,
  isStaff,
  onDocumentPreview,
  onDocumentOpenInNewTab,
  onSigningDialogClose,
  onSigningDialogOpen,
  onSigningCompleted,
  onRetrySignatureConfirmation,
}: SubmissionDisplayStaticProps) {
  const { user } = useCurrentUser();
  const theme = useTheme();
  const submissionStatus = submission.status;

  // Check if current user is a signer on this submission
  const isSigner = reviewers.some((reviewer) => reviewer.isSigner && reviewer.user.id === user?.id);

  return (
    <Grid container spacing={4}>
      {/* Don't show this to staff because it's telling non staff users Encamp staff are working on it */}
      {!isStaff &&
        reviewState !== SubmissionReviewState.REVIEWER_WAITING_FOR_TURN &&
        reviewState !== SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS &&
        [
          SubmissionStatus.NotStarted,
          SubmissionStatus.InProgress,
          SubmissionStatus.EncampInitialReview,
        ].includes(submissionStatus) && (
          <Grid size={12}>
            <Alert severity="warning" icon={<HourglassTopIcon />} sx={{ borderRadius: "4px" }}>
              <Typography fontWeight="bold" marginBottom={0.5}>
                Submission not ready for review
              </Typography>
              <Typography variant="body2">
                This submission is being worked on by Encamp Staff and the reviewer(s) and signer
                will be notified when it is ready for review.
              </Typography>
            </Alert>
          </Grid>
        )}

      {reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS && (
        <Grid size={12}>
          <Alert severity="warning" icon={<HourglassTopIcon />} sx={{ borderRadius: "4px" }}>
            <Typography fontWeight="bold" marginBottom={0.5}>
              Submission not ready for signature
            </Typography>
            <Typography variant="body2">
              This submission cannot be signed until all preceding reviews are complete. We will
              notify the signatory via email once it is ready signing.
            </Typography>
          </Alert>
        </Grid>
      )}

      {reviewState === SubmissionReviewState.REVIEWER_WAITING_FOR_TURN && (
        <Grid size={12}>
          <Alert severity="warning" icon={<HourglassTopIcon />} sx={{ borderRadius: "4px" }}>
            <Typography fontWeight="bold" marginBottom={0.5}>
              Submission is not ready for review
            </Typography>
            <Typography variant="body2">
              The submission cannot be reviewed until all preceding reviews are complete. We will
              notify you via email once it is ready for review.
            </Typography>
          </Alert>
        </Grid>
      )}
      {submission.status === SubmissionStatus.ProcessingSubmission && (
        <Grid size={12}>
          <Alert severity="warning" icon={<HourglassTopIcon />} sx={{ borderRadius: "4px" }}>
            <Typography fontWeight="bold" marginBottom={0.5}>
              Submission is being processed by Encamp Staff
            </Typography>
            <Typography variant="body2">Submission will be updated when completed</Typography>
          </Alert>
        </Grid>
      )}
      <Grid size={{ xs: 12, md: 6 }}>
        <Stack spacing={3}>
          <SubmissionReadOnlySummary
            tenantId={tenantId}
            isStaff={isStaff}
            facilities={facilities}
            reviewers={reviewers}
            signer={signer}
            title={submission.title}
            dueDate={submission.dueDate}
            recipient={submission.recipient}
            staffAssignee={submission.staffAssignee}
            manifest={submission.processedManifestExceptionReport}
            notes={submission.notes}
            signerExtra={
              submission.signatureType === SignatureType.Digital ? (
                <SignatureConfirmationAlert
                  signingCompleted={signingCompleted}
                  signingFlowConfirmed={signingFlowConfirmed}
                  confirmationState={signatureConfirmationState}
                  isAwaitingPostSignatureTransition={isAwaitingPostSignatureTransition}
                  onRetry={onRetrySignatureConfirmation}
                  sx={{ py: 0.5, px: 1.5 }}
                />
              ) : undefined
            }
          />
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Submission Document
            </Typography>
            {submissionDocuments.length > 0 ? (
              <Stack spacing={1}>
                {submissionDocuments.map((document) => {
                  const isRejected =
                    document.submissionDocumentType === SubmissionDocumentType.RejectedSubmission;

                  return (
                    <Box key={document.id} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body1">{document.name}</Typography>
                      {isRejected && (
                        <Chip
                          label="Rejected"
                          size="small"
                          sx={{
                            ml: 1,
                            color: theme.palette.error.main,
                            backgroundColor: brandColors["encamp-red"][200],
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        onClick={() => onDocumentPreview({ id: document.id, name: document.name })}
                        sx={{ color: theme.palette.grey[400] }}
                        aria-label="Preview document"
                      >
                        <PreviewIcon fontSize="small" />
                      </IconButton>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={() => onDocumentOpenInNewTab({ id: document.id })}
                          sx={{
                            color: theme.palette.action.active,
                          }}
                          aria-label={`Download ${document.name}`}
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No submission documents
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Additional Attachment(s)
            </Typography>
            {attachmentDocuments.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>File Name</TableCell>
                      <TableCell>Document Type</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attachmentDocuments.map((document) => {
                      const documentType = document?.documentType || "Unknown Type";

                      return (
                        <TableRow key={document.id}>
                          <TableCell>{document.name}</TableCell>
                          <TableCell>{documentType}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  onDocumentPreview({ id: document.id, name: document.name })
                                }
                                sx={{ color: theme.palette.grey[400] }}
                                aria-label="Preview document"
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                              <Tooltip title="Download">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    onDocumentOpenInNewTab({
                                      id: document.id,
                                    })
                                  }
                                  sx={{
                                    color: theme.palette.action.active,
                                  }}
                                  aria-label={`Download ${document.name}`}
                                >
                                  <OpenInNew fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No additional attachments
              </Typography>
            )}
          </Box>
          <SubmissionPaymentDisplay submissionPayments={submission.payments || []} />
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        {previewDocumentUrl ? (
          <SubmissionDocumentPreview
            previewDocumentUrl={previewDocumentUrl}
            fileExtension={previewDocumentExtension}
            reviewState={reviewState}
            signingCompleted={signingCompleted}
            signingPending={signingConfirmationPending}
            signatureType={submission.signatureType}
            submissionStatus={submission.status}
            isDocumentPreparedForSigning={isPreviewedDocumentPreparedForSigning}
            isSigner={isSigner}
            onSigningDialogOpen={onSigningDialogOpen}
          />
        ) : (
          <Paper
            sx={{
              p: 3,
              height: "100%",
              minHeight: 600,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "none",
              backgroundColor: theme.palette.background.paper,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              align="center"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "14px",
                lineHeight: 1.5,
              }}
            >
              {submissionDocuments.length > 0 || attachmentDocuments.length > 0
                ? "Click on a document chip above to preview"
                : "No documents available for preview"}
            </Typography>
          </Paper>
        )}
        {!!signingUrlData?.getEmbeddedDocumentSigningUrl && (
          <EmbeddedDocumentSigning
            open={signingDialogOpen}
            onSigned={onSigningCompleted}
            onClose={onSigningDialogClose}
            signingUrl={signingUrlData.getEmbeddedDocumentSigningUrl}
          />
        )}
      </Grid>
    </Grid>
  );
}
